import { useEffect, useState } from 'react';
import { useList } from '@refinedev/core';
import type { TfTrackRow } from '@verybigthings/trackframe-shared/schemas';
import { trackLabel } from '@verybigthings/trackframe-shared/tracks';
import { hub } from '@/lib/hub-client';
import { rootRedirectTarget } from '@/lib/landing-url';
import { cn } from '@/lib/utils';

// ─── single track card ─────────────────────────────────────────────────────

// One card per track: display name + description, nothing else (HUB-125).
// Styled with the theme tokens only (bg-card, border, text-muted-foreground)
// so the page follows the Feel / Mode / Palette picked in the admin.
export function TrackCard({ track }: { track: TfTrackRow }) {
  const enabled = Boolean(track.baseUrl);
  const body = (
    <>
      <span className="text-[17px] font-semibold leading-snug">{trackLabel(track)}</span>
      {track.description ? (
        <span className="text-sm leading-relaxed text-muted-foreground line-clamp-3">
          {track.description}
        </span>
      ) : null}
      {!enabled && (
        <span className="text-sm leading-relaxed text-muted-foreground">
          No frontend URL configured.
        </span>
      )}
    </>
  );
  const base = 'flex min-h-[132px] flex-col gap-2 rounded-lg p-5 text-card-foreground';

  if (!enabled) {
    return (
      <div className={cn(base, 'border border-dashed bg-muted/40 opacity-70')}>{body}</div>
    );
  }
  return (
    <a
      href={track.baseUrl as string}
      className={cn(
        base,
        'border bg-card no-underline transition-colors hover:border-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      )}
    >
      {body}
    </a>
  );
}

// ─── full-page wrapper states ──────────────────────────────────────────────

function Page({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-full bg-background px-6 pb-16 pt-12 text-foreground">
      <div className="mx-auto flex w-full max-w-[1040px] flex-col gap-6">
        <div className="flex flex-col gap-1">
          <h1 className="text-[32px] font-medium leading-tight">Tracks</h1>
          <p className="text-sm text-muted-foreground">Open a track to start working.</p>
        </div>
        {children}
      </div>
    </div>
  );
}

function Notice({ title, text }: { title: string; text: string }) {
  return (
    <div className="rounded-lg border bg-card p-5 text-card-foreground">
      <p className="font-semibold">{title}</p>
      <p className="mt-1 text-sm text-muted-foreground">{text}</p>
    </div>
  );
}

// ─── hub home ─────────────────────────────────────────────────────────────

// HUB-125 D4: a set landing URL replaces this page — `/` sends the signed-in
// user there. A fresh page load is handled before anything mounts
// (`bootRedirect` in main.tsx); this hook covers in-app navigation to `/`
// (header logo, catch-all route). Null until the hub has answered, so the
// list never flashes before a redirect; `false` means "render the list".
function useRootRedirect(): string | null | false {
  const [target, setTarget] = useState<string | null | false>(null);
  useEffect(() => {
    hub
      .settingsBranding()
      .then((b) => setTarget(rootRedirectTarget(b.landingUrl, window.location.origin) ?? false))
      .catch(() => setTarget(false));
  }, []);
  useEffect(() => {
    if (target) window.location.replace(target);
  }, [target]);
  return target;
}

// Hub root page: one card per track the signed-in user can read. Cards link
// to `track.baseUrl`; tracks without one render dimmed so admins still see
// the gap. Replaced by the landing URL when an admin set one (D4).
export function TracksHome() {
  const redirect = useRootRedirect();
  const { query } = useList<TfTrackRow>({
    resource: 'tracks',
    pagination: { mode: 'off' },
  });

  // Nothing at all while deciding — the "Tracks" page must not paint under
  // a redirect, not even as a skeleton.
  if (redirect !== false) return null;

  if (query.isLoading) {
    return (
      <Page>
        <p className="text-sm text-muted-foreground">Loading tracks…</p>
      </Page>
    );
  }

  if (query.error) {
    const message = (query.error as { message?: string }).message ?? 'Unknown error';
    return (
      <Page>
        <Notice title="Failed to load tracks" text={message} />
      </Page>
    );
  }

  const tracks: TfTrackRow[] = query.data?.data ?? [];

  if (tracks.length === 0) {
    return (
      <Page>
        <Notice
          title="No tracks yet"
          text="Register your first track in the admin to see it here."
        />
      </Page>
    );
  }

  return (
    <Page>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4">
        {tracks.map((track) => (
          <TrackCard key={track.id} track={track} />
        ))}
      </div>
    </Page>
  );
}
